import type { MouseEvent } from 'react';
import { HStack, Text, chakra } from '@chakra-ui/react';
import { MdFavorite, MdFavoriteBorder } from 'react-icons/md';
import { formatLikes } from '../../utils/formatters';

interface DeckLikeCountProps {
  count: number;
  fontSize?: string;
  color?: string;
  fontWeight?: string;
  /** Heart glyph strength. Defaults to a quiet mark. */
  iconOpacity?: number;
  /** When set, the count is a like / unlike control. */
  liked?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
}

export function DeckLikeCount({
  count,
  fontSize = 'xs',
  color = 'textMuted',
  fontWeight,
  iconOpacity = 0.5,
  liked = false,
  disabled = false,
  onClick,
}: DeckLikeCountProps) {
  const icon = liked
    ? <MdFavorite style={{ flexShrink: 0 }} />
    : <MdFavoriteBorder style={{ opacity: onClick ? 1 : iconOpacity, flexShrink: 0 }} />;
  const label = (
    <Text fontSize={fontSize} fontWeight={fontWeight} lineHeight="1">{formatLikes(count)}</Text>
  );

  if (!onClick) {
    return (
      <HStack gap={1} color={color} flexShrink={0}>
        {liked ? <MdFavorite style={{ flexShrink: 0 }} /> : <MdFavorite style={{ opacity: iconOpacity, flexShrink: 0 }} />}
        {label}
      </HStack>
    );
  }

  return (
    <chakra.button
      type="button"
      display="inline-flex"
      alignItems="center"
      gap={1}
      flexShrink={0}
      bg="transparent"
      borderWidth={0}
      p={0}
      font="inherit"
      lineHeight="1"
      color={liked ? { base: 'red.600', _dark: 'red.300' } : color}
      cursor={disabled ? 'wait' : 'pointer'}
      aria-label={liked ? 'Unlike deck' : 'Like deck'}
      aria-pressed={liked}
      disabled={disabled}
      onClick={onClick}
      _hover={{ color: liked ? { base: 'red.700', _dark: 'red.200' } : 'text' }}
      _disabled={{ opacity: 0.6, cursor: 'wait' }}
    >
      {icon}
      {label}
    </chakra.button>
  );
}
